// Shows imbalanced debit/credit subtotals and hover highlighting for related letters.
import React from "react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { appScrollbarSx } from "../../../lib/scrollbarSx";
import type { RelatedSubtotalRow } from "../types";

interface SheetRelatedTotalsPanelProps {
  subtotals: RelatedSubtotalRow[];
  hoveredLetter: string | null;
  onHoverLetterChange: (letter: string | null) => void;
  formatAmount: (value: number) => string;
}

export default function SheetRelatedTotalsPanel({
  subtotals,
  hoveredLetter,
  onHoverLetterChange,
  formatAmount,
}: SheetRelatedTotalsPanelProps) {
  return (
    <Box
      className="export-hide-divider"
      sx={{
        gridColumn: "2 / 3",
        gridRow: "1 / 5",
        borderLeft: "1px solid",
        borderColor: (theme) => (
          theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.22)
            : alpha(theme.palette.common.black, 0.12)
        ),
        p: 2,
        overflow: "auto",
        ...appScrollbarSx,
        cursor: "default",
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, textAlign: "center" }}>
        Debits ≠ Credits
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "calc(1ch + 12px) 1fr 1fr",
          columnGap: 1,
          rowGap: 0.75,
          alignItems: "center",
        }}
      >
        <Box />
        <Typography variant="caption" fontWeight={700} textAlign="right">Debits</Typography>
        <Typography variant="caption" fontWeight={700} textAlign="right">Credits</Typography>

        {subtotals.map((subtotal) => {
          const isHovered = hoveredLetter === subtotal.letter;

          return (
            <Box
              key={subtotal.letter}
              onMouseEnter={() => onHoverLetterChange(subtotal.letter)}
              onMouseLeave={() => onHoverLetterChange(null)}
              sx={{
                gridColumn: "1 / -1",
                display: "grid",
                gridTemplateColumns: "calc(1ch + 12px) 1fr 1fr",
                columnGap: 1,
                alignItems: "center",
                borderRadius: 0.75,
                px: 0.25,
                cursor: "default",
                backgroundColor: isHovered ? "rgba(25, 118, 210, 0.08)" : "transparent",
              }}
            >
              <Typography variant="body2">{subtotal.letter}</Typography>
              <Typography variant="body2" textAlign="right">{formatAmount(subtotal.debitSubtotal)}</Typography>
              <Typography variant="body2" textAlign="right">{formatAmount(subtotal.creditSubtotal)}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
